import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, DatePicker, Input, Message, SelectPicker, Tooltip, Whisper, toaster } from 'rsuite';
import { Button as NsiButton, Modal, useLastPresent } from '@nsi-advisory-services/components';

import {
  useProjects,
  useProjectMilestones,
  useProjectUsers,
  useProjectUserAssignmentSummary,
  useSetProjectMember,
  useRemoveProjectMember,
  useCreateMilestone,
  useUpdateMilestone,
  useDeleteMilestone,
  useUpdateProject,
  useArchiveProject,
  useCreateTaskOrder,
  useUpdateTaskOrder,
  useDeleteTaskOrder,
  useUploadProjectPhoto,
  useDeleteProjectPhoto,
  useTaskOrders,
  useUsers,
} from '../hooks/useQueries';
import { formatHours } from '../utils/chartUtils';
import ProjectLocationPicker from '../components/ProjectLocationPicker';
import usePermissions from '../hooks/usePermissions';
import ProjectRightsChecklist from '../components/ProjectRightsChecklist';
// TaskOrderTimesheetControl (per task order Add / Link) stays off until task orders move to the timesheet.
import { ProjectTimesheetSection } from '../components/TimesheetLink';
import { ProjectPursuitLinkField } from '../components/PursuitLink';
import './ProjectDetailPage.css';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const formatCurrency = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return currencyFormatter.format(n);
};

const digitsOnly = (raw) => String(raw ?? '').replace(/[^0-9]/g, '');

const parseMoney = (raw) => {
  const s = String(raw ?? '').replace(/[$,\s]/g, '').trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const amountsEqual = (a, b) => {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  return Math.abs(Number(a) - Number(b)) < 0.0001;
};

const parseCoordinate = (raw) => {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const formatShortDate = (iso) => {
  const s = typeof iso === 'string' ? iso.slice(0, 10) : null;
  if (!s) return '—';
  const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(s);
  if (!m) return '—';
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return '—';
  const d = new Date(year, month - 1, day);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const parseDate10Parts = (raw) => {
  const s = typeof raw === 'string' ? raw.slice(0, 10) : '';
  const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(s);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  return { y, m: mo, d };
};

const utcDayNumber = (parts) => {
  if (!parts) return null;
  // months are 1-based in parts, 0-based in Date.UTC
  return Math.floor(Date.UTC(parts.y, parts.m - 1, parts.d) / (24 * 60 * 60 * 1000));
};

const todayParts = () => {
  const now = new Date();
  return { y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate() };
};

const plural = (n, unit) => `${n} ${unit}${n === 1 ? '' : 's'}`;

const diffFullMonths = (fromParts, toParts) => {
  if (!fromParts || !toParts) return null;
  let months = (toParts.y - fromParts.y) * 12 + (toParts.m - fromParts.m);
  if (toParts.d < fromParts.d) months -= 1;
  return Math.max(0, months);
};

const formatSpan = (fromParts, toParts) => {
  const fromDay = utcDayNumber(fromParts);
  const toDay = utcDayNumber(toParts);
  if (fromDay === null || toDay === null) return null;
  const days = toDay - fromDay;
  if (days < 0) return null;
  if (days === 0) return plural(0, 'day');

  if (days < 14) return plural(days, 'day');

  if (days < 60) {
    const weeks = Math.floor(days / 7);
    return weeks >= 2 ? plural(weeks, 'week') : plural(days, 'day');
  }

  const monthsTotal = diffFullMonths(fromParts, toParts);
  if (monthsTotal === null) return null;
  const years = Math.floor(monthsTotal / 12);
  const months = monthsTotal % 12;

  if (years > 0) return months > 0 ? `${plural(years, 'year')} ${plural(months, 'month')}` : plural(years, 'year');
  if (months > 0) return plural(months, 'month');

  const weeks = Math.floor(days / 7);
  return weeks >= 1 ? plural(weeks, 'week') : plural(days, 'day');
};

const formatDuration = (startIso, endIso) => {
  const startParts = parseDate10Parts(startIso);
  const endParts = parseDate10Parts(endIso);
  if (!startParts || !endParts) return '—';
  const span = formatSpan(startParts, endParts);
  if (!span) return '—';
  return span;
};

const formatTimeRemaining = (startIso, endIso) => {
  const endParts = parseDate10Parts(endIso);
  if (!endParts) return null;

  const startParts = parseDate10Parts(startIso);
  const today = todayParts();

  const todayDay = utcDayNumber(today);
  const startDay = startParts ? utcDayNumber(startParts) : null;
  const endDay = utcDayNumber(endParts);
  if (todayDay === null || endDay === null) return null;

  if (endDay < todayDay) return 'Ended';
  if (endDay === todayDay) return 'Ends today';

  if (startDay !== null && startDay > todayDay) {
    const span = formatSpan(today, startParts);
    return span ? `Starts in ${span}` : null;
  }

  const span = formatSpan(today, endParts);
  return span ? `${span} remaining` : null;
};

const normalizeDate10 = (raw) => {
  if (raw instanceof Date) {
    const t = raw.getTime();
    if (Number.isNaN(t)) return null;
    const y = raw.getFullYear();
    const m = String(raw.getMonth() + 1).padStart(2, '0');
    const d = String(raw.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const s = typeof raw === 'string' ? raw.slice(0, 10) : '';
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
};

const isoDateToDate = (raw) => {
  const iso = normalizeDate10(raw);
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7));
  const d = Number(iso.slice(8, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  const out = new Date(y, m - 1, d);
  out.setHours(0, 0, 0, 0);
  return Number.isNaN(out.getTime()) ? null : out;
};

export default function ProjectDetailPage() {
  const navigate = useNavigate();
  const params = useParams();
  const projectId = Number(params?.projectId);
  const permissions = usePermissions();
  // Each control is gated on the one project right the server checks for it.
  const canManage = permissions.canProject(projectId, 'manage');
  const canFinance = permissions.canProject(projectId, 'finance');

  const {
    data: projects = [],
    isLoading: projectsLoading,
    error: projectsError,
  } = useProjects();

  const project = useMemo(() => {
    if (!Number.isInteger(projectId)) return null;
    return (projects || []).find((p) => Number(p?.id) === projectId) ?? null;
  }, [projects, projectId]);

  const updateProject = useUpdateProject({
    onSuccess: async () => {
      toaster.push(
        <Message showIcon type="success" closable>
          Project updated.
        </Message>,
        { placement: 'topEnd' }
      );
    },
  });

  const createTaskOrder = useCreateTaskOrder({
    onSuccess: async () => {
      toaster.push(
        <Message showIcon type="success" closable>
          Task order created.
        </Message>,
        { placement: 'topEnd' }
      );
    },
  });

  const updateTaskOrder = useUpdateTaskOrder({
    onSuccess: async () => {
      toaster.push(
        <Message showIcon type="success" closable>
          Task order updated.
        </Message>,
        { placement: 'topEnd' }
      );
    },
  });

  const deleteTaskOrder = useDeleteTaskOrder({
    onSuccess: async (_data, variables) => {
      const deletedName = String(variables?.taskOrderName || 'Task order');
      toaster.push(
        <Message showIcon type="success" closable>
          {deletedName} deleted.
        </Message>,
        { placement: 'topEnd' }
      );
    },
  });

  const uploadProjectPhoto = useUploadProjectPhoto({
    onSuccess: async () => {
      toaster.push(
        <Message showIcon type="success" closable>
          Project photo uploaded.
        </Message>,
        { placement: 'topEnd' }
      );
      setProjectPhotoModalOpen(false);
    },
  });

  const deleteProjectPhoto = useDeleteProjectPhoto({
    onSuccess: async () => {
      toaster.push(
        <Message showIcon type="success" closable>
          Project photo removed.
        </Message>,
        { placement: 'topEnd' }
      );
      setConfirmPhotoDeleteOpen(false);
    },
  });

  const [isHeroEditing, setIsHeroEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editPicUserId, setEditPicUserId] = useState(null);
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editLocationCity, setEditLocationCity] = useState('');
  const [editLocationLat, setEditLocationLat] = useState(null);
  const [editLocationLng, setEditLocationLng] = useState(null);
  const [editBudgetTarget, setEditBudgetTarget] = useState('');
  const [budgetOverrideEnabled, setBudgetOverrideEnabled] = useState(false);
  const [editContractNumber, setEditContractNumber] = useState('');
  const [projectPhotoModalOpen, setProjectPhotoModalOpen] = useState(false);
  const [confirmPhotoDeleteOpen, setConfirmPhotoDeleteOpen] = useState(false);
  const projectPhotoInputRef = useRef(null);
  const budgetDrivenWhisperRef = useRef(null);

  const [addTaskOrderOpen, setAddTaskOrderOpen] = useState(false);
  const [newTaskOrderName, setNewTaskOrderName] = useState('');
  const [newTaskOrderStartDate, setNewTaskOrderStartDate] = useState('');
  const [newTaskOrderEndDate, setNewTaskOrderEndDate] = useState('');
  const [newTaskOrderBudgetTarget, setNewTaskOrderBudgetTarget] = useState('');
  const [editTaskOrderOpen, setEditTaskOrderOpen] = useState(false);
  const [pendingEditTaskOrder, setPendingEditTaskOrder] = useState(null);
  const [editTaskOrderName, setEditTaskOrderName] = useState('');
  const [editTaskOrderStartDate, setEditTaskOrderStartDate] = useState('');
  const [editTaskOrderEndDate, setEditTaskOrderEndDate] = useState('');
  const [editTaskOrderBudgetTarget, setEditTaskOrderBudgetTarget] = useState('');
  const [pendingDeleteTaskOrder, setPendingDeleteTaskOrder] = useState(null);
  const pendingDeleteTaskOrderShown = useLastPresent(pendingDeleteTaskOrder); // keeps the dialog steady while it fades out
  const [taskOrderDeleteConfirmText, setTaskOrderDeleteConfirmText] = useState('');

  const {
    data: users = [],
    isLoading: usersLoading,
    error: usersError,
  } = useUsers();

  const [milestoneModalOpen, setMilestoneModalOpen] = useState(false);
  const [milestoneModalMode, setMilestoneModalMode] = useState('create');
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [milestoneNameInput, setMilestoneNameInput] = useState('');
  const [milestoneDateInput, setMilestoneDateInput] = useState(null);
  const [pendingDeleteMilestone, setPendingDeleteMilestone] = useState(null);
  const pendingDeleteMilestoneShown = useLastPresent(pendingDeleteMilestone); // keeps the dialog steady while it fades out

  const {
    data: projectUsersData,
    isLoading: projectUsersLoading,
    error: projectUsersError,
  } = useProjectUsers(projectId, { enabled: Number.isInteger(projectId) });

  const projectUsers = useMemo(() => (Array.isArray(projectUsersData) ? projectUsersData : []), [projectUsersData]);

  const {
    data: milestonesData,
    isLoading: milestonesLoading,
    error: milestonesError,
  } = useProjectMilestones(projectId, { enabled: Number.isInteger(projectId) });

  const milestones = useMemo(() => {
    const rows = Array.isArray(milestonesData) ? milestonesData : [];
    return [...rows].sort((a, b) => {
      const ad = String(a?.milestone_date || '');
      const bd = String(b?.milestone_date || '');
      if (ad !== bd) return ad.localeCompare(bd);
      return String(a?.name || '').localeCompare(String(b?.name || ''));
    });
  }, [milestonesData]);

  // Auto milestones are derived from task order dates and can only be deleted; custom
  // ones are hand-entered and editable. They get a column each so the two kinds never
  // interleave in one list.
  const autoMilestones = useMemo(() => milestones.filter((m) => Boolean(m?.is_auto)), [milestones]);
  const customMilestones = useMemo(() => milestones.filter((m) => !m?.is_auto), [milestones]);

  const createMilestone = useCreateMilestone();
  const updateMilestone = useUpdateMilestone();

  const deleteMilestone = useDeleteMilestone();

  const projectStartIso = useMemo(() => normalizeDate10(project?.startDate ?? project?.start_date), [project]);
  const projectEndIso = useMemo(() => normalizeDate10(project?.endDate ?? project?.end_date), [project]);

  useEffect(() => {
    if (!project) return;
    setEditName(String(project?.name ?? ''));
    setEditPicUserId(Number.isInteger(Number(project?.picUserId)) ? Number(project.picUserId) : null);
    setEditStartDate(normalizeDate10(project?.startDate ?? project?.start_date) ?? '');
    setEditEndDate(normalizeDate10(project?.endDate ?? project?.end_date) ?? '');
    setEditLocationCity(String(project?.locationCity ?? project?.location_city ?? ''));
    setEditLocationLat(parseCoordinate(project?.locationLat ?? project?.location_lat));
    setEditLocationLng(parseCoordinate(project?.locationLng ?? project?.location_lng));
    setEditBudgetTarget(
      project?.budgetTarget === null || project?.budgetTarget === undefined
        ? ''
        : formatCurrency(project.budgetTarget)
    );
    setEditContractNumber(String(project?.contractNumber ?? project?.contract_number ?? ''));
  }, [project]);

  const heroDraftSnapshot = useMemo(() => ({
    name: String(editName || '').trim(),
    picUserId: Number.isInteger(Number(editPicUserId)) ? Number(editPicUserId) : null,
    startDate: normalizeDate10(editStartDate),
    endDate: normalizeDate10(editEndDate),
    locationCity: String(editLocationCity || '').trim() || null,
    locationLat: parseCoordinate(editLocationLat),
    locationLng: parseCoordinate(editLocationLng),
    budgetTarget: parseMoney(editBudgetTarget),
    contractNumber: String(editContractNumber || '').trim() || null,
  }), [
    editName,
    editPicUserId,
    editStartDate,
    editEndDate,
    editLocationCity,
    editLocationLat,
    editLocationLng,
    editBudgetTarget,
    editContractNumber,
  ]);

  const heroSavedSnapshot = useMemo(() => {
    if (!project) return null;
    const rawBudget = project?.budgetTarget ?? project?.budget_target;
    const budgetValue = rawBudget === null || rawBudget === undefined ? null : Number(rawBudget);
    return {
      name: String(project?.name || '').trim(),
      picUserId: Number.isInteger(Number(project?.picUserId)) ? Number(project.picUserId) : null,
      startDate: normalizeDate10(project?.startDate ?? project?.start_date),
      endDate: normalizeDate10(project?.endDate ?? project?.end_date),
      locationCity: String(project?.locationCity ?? project?.location_city ?? '').trim() || null,
      locationLat: parseCoordinate(project?.locationLat ?? project?.location_lat),
      locationLng: parseCoordinate(project?.locationLng ?? project?.location_lng),
      budgetTarget: Number.isFinite(budgetValue) ? budgetValue : null,
      contractNumber: String(project?.contractNumber ?? project?.contract_number ?? '').trim() || null,
    };
  }, [project]);

  const canSaveHero = useMemo(() => {
    if (!Number.isInteger(projectId)) return false;
    if (!String(editName || '').trim()) return false;
    const s = normalizeDate10(editStartDate);
    const e = normalizeDate10(editEndDate);
    if (!s || !e) return false;
    if (String(e).localeCompare(String(s)) < 0) return false;
    if (updateProject.isPending) return false;
    return JSON.stringify(heroDraftSnapshot) !== JSON.stringify(heroSavedSnapshot);
  }, [projectId, editName, editStartDate, editEndDate, updateProject.isPending, heroDraftSnapshot, heroSavedSnapshot]);

  const startHeroEdit = () => {
    const baseBudget = hasBudgetOverride ? projectBudgetValue : drivenBudgetValue;
    setBudgetOverrideEnabled(hasBudgetOverride);
    setEditBudgetTarget(baseBudget === null || baseBudget === undefined ? '' : formatCurrency(baseBudget));
    setIsHeroEditing(true);
  };

  const cancelHeroEdit = () => {
    if (!project) return;
    setEditName(String(project?.name ?? ''));
    setEditPicUserId(Number.isInteger(Number(project?.picUserId)) ? Number(project.picUserId) : null);
    setEditStartDate(normalizeDate10(project?.startDate ?? project?.start_date) ?? '');
    setEditEndDate(normalizeDate10(project?.endDate ?? project?.end_date) ?? '');
    setEditLocationCity(String(project?.locationCity ?? project?.location_city ?? ''));
    setEditLocationLat(parseCoordinate(project?.locationLat ?? project?.location_lat));
    setEditLocationLng(parseCoordinate(project?.locationLng ?? project?.location_lng));
    const baseBudget = hasBudgetOverride ? projectBudgetValue : drivenBudgetValue;
    setBudgetOverrideEnabled(hasBudgetOverride);
    setEditBudgetTarget(baseBudget === null || baseBudget === undefined ? '' : formatCurrency(baseBudget));
    setEditContractNumber(String(project?.contractNumber ?? project?.contract_number ?? ''));
    setIsHeroEditing(false);
  };

  const saveHeroEdit = async () => {
    if (!Number.isInteger(projectId) || !canSaveHero) return;
    const payload = {
      name: String(editName || '').trim(),
      picUserId: Number.isInteger(Number(editPicUserId)) ? Number(editPicUserId) : null,
      startDate: normalizeDate10(editStartDate),
      endDate: normalizeDate10(editEndDate),
      locationCity: String(editLocationCity || '').trim() || null,
      locationLat: parseCoordinate(editLocationLat),
      locationLng: parseCoordinate(editLocationLng),
      // Only someone with Manage Finance sends the budget; for anyone else it stays as it is.
      ...(canFinance
        ? { budgetTarget: hasExplicitTaskOrders && !budgetOverrideEnabled ? taskOrderBudgetTotal : parseMoney(editBudgetTarget) }
        : {}),
      contractNumber: String(editContractNumber || '').trim() || null,
    };
    await updateProject.mutateAsync({ projectId, payload });
    setIsHeroEditing(false);
  };

  const uploadProjectPhotoFile = async (file) => {
    if (!Number.isInteger(projectId)) return;
    if (!file) return;

    if (!String(file.type || '').startsWith('image/')) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please select an image file.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Image is too large (max 5MB).
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const toBase64 = () =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const full = String(reader.result || '');
          const commaIdx = full.indexOf(',');
          const b64 = commaIdx >= 0 ? full.slice(commaIdx + 1) : full;
          resolve(b64);
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });

    try {
      const dataBase64 = await toBase64();
      await uploadProjectPhoto.mutateAsync({
        projectId,
        payload: {
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          dataBase64,
        },
      });
    } catch (err) {
      toaster.push(
        <Message showIcon type="error" closable>
          Failed to upload photo: {String(err?.message || err)}
        </Message>,
        { placement: 'topEnd' }
      );
    }
  };

  const onSelectProjectPhoto = async (event) => {
    const file = event?.target?.files?.[0];
    try {
      await uploadProjectPhotoFile(file);
    } finally {
      if (event?.target) event.target.value = '';
    }
  };

  // How far through the project schedule today falls, for the rail between the two dates.
  const scheduleProgress = useMemo(() => {
    const startDay = utcDayNumber(parseDate10Parts(projectStartIso));
    const endDay = utcDayNumber(parseDate10Parts(projectEndIso));
    const todayDay = utcDayNumber(todayParts());
    if (startDay === null || endDay === null || todayDay === null || endDay <= startDay) return null;

    const ratio = Math.min(1, Math.max(0, (todayDay - startDay) / (endDay - startDay)));
    return {
      percent: ratio * 100,
      isComplete: todayDay >= endDay,
      daysRemaining: Math.max(0, endDay - todayDay),
    };
  }, [projectStartIso, projectEndIso]);

  const milestoneDateIso = useMemo(() => normalizeDate10(milestoneDateInput), [milestoneDateInput]);

  const isMilestoneInProjectRange = useMemo(() => {
    if (!milestoneDateIso) return false;
    if (projectStartIso && String(milestoneDateIso).localeCompare(String(projectStartIso)) < 0) return false;
    if (projectEndIso && String(milestoneDateIso).localeCompare(String(projectEndIso)) > 0) return false;
    return true;
  }, [milestoneDateIso, projectStartIso, projectEndIso]);

  const milestoneFormValid = useMemo(() => {
    if (!milestoneNameInput.trim()) return false;
    if (!milestoneDateIso) return false;
    if (!isMilestoneInProjectRange) return false;
    if (createMilestone.isPending || updateMilestone.isPending) return false;
    return true;
  }, [milestoneNameInput, milestoneDateIso, isMilestoneInProjectRange, createMilestone.isPending, updateMilestone.isPending]);

  const openCreateMilestoneModal = () => {
    setMilestoneModalMode('create');
    setEditingMilestone(null);
    setMilestoneNameInput('');
    setMilestoneDateInput(null);
    setMilestoneModalOpen(true);
  };

  const openEditMilestoneModal = (milestone) => {
    if (!milestone || milestone.is_auto) return;
    setMilestoneModalMode('edit');
    setEditingMilestone(milestone);
    setMilestoneNameInput(String(milestone?.name || ''));
    setMilestoneDateInput(isoDateToDate(milestone?.milestone_date));
    setMilestoneModalOpen(true);
  };

  const closeMilestoneModal = () => {
    setMilestoneModalOpen(false);
  };

  // Runs once the dialog has faded out (onExited), so its fields don't empty while it closes.
  const resetMilestoneModal = () => {
    setEditingMilestone(null);
    setMilestoneNameInput('');
    setMilestoneDateInput(null);
  };

  const saveMilestone = async () => {
    if (!Number.isInteger(projectId) || !milestoneFormValid) return;
    const payload = {
      name: milestoneNameInput.trim(),
      milestone_date: milestoneDateIso,
    };

    if (milestoneModalMode === 'edit') {
      const milestoneId = Number(editingMilestone?.id);
      if (!Number.isInteger(milestoneId)) return;
      await updateMilestone.mutateAsync({ projectId, milestoneId, payload });
      closeMilestoneModal();
      return;
    }

    await createMilestone.mutateAsync({ projectId, payload });
    closeMilestoneModal();
  };

  const confirmDeleteMilestone = async () => {
    if (!Number.isInteger(projectId) || !pendingDeleteMilestone) return;
    const milestoneId = pendingDeleteMilestone?.id;
    if (milestoneId === null || milestoneId === undefined || milestoneId === '') return;

    try {
      await deleteMilestone.mutateAsync({ projectId, milestoneId });
      setPendingDeleteMilestone(null);
    } catch {
      // error is already surfaced by mutation state/UI
    }
  };

  const allUserOptions = useMemo(() => {
    return (users || [])
      .map((u) => ({ label: u?.name || '—', value: Number(u?.id) }))
      .filter((o) => Number.isInteger(o.value));
  }, [users]);

  // --- Archive the project ---
  // Archiving is reversible (Configuration > Project Management restores it),
  // but it pulls the project out of every list, so it goes behind a confirm.
  const [archiveOpen, setArchiveOpen] = useState(false);
  const archiveProject = useArchiveProject();

  const submitArchiveProject = async () => {
    if (!Number.isInteger(projectId)) return;
    try {
      await archiveProject.mutateAsync(projectId);
      setArchiveOpen(false);
      navigate('/', { replace: true });
    } catch {
      // the mutation's error state is surfaced in the modal
    }
  };

  // --- Project members and their rights ---
  // Adding someone means choosing their rights on this project with them; none means they can
  // view it. Rights are given, changed and taken away one member at a time, and only within the
  // rights the signed-in user holds here (the server refuses the rest).
  const memberToast = (text) =>
    toaster.push(
      <Message showIcon type="success" closable>
        {text}
      </Message>,
      { placement: 'topEnd' }
    );
  const setProjectMember = useSetProjectMember();
  const removeProjectMember = useRemoveProjectMember({ onSuccess: async () => memberToast('Removed from the project.') });
  const membersBusy = setProjectMember.isPending || removeProjectMember.isPending;
  const rightsCatalog = permissions.projectRights;
  const grantable = permissions.grantableRights(projectId);
  const rightsTitle = (rights) =>
    rights?.length
      ? rights.map((key) => rightsCatalog.find((r) => r.key === key)?.label || key).join(', ')
      : 'Can view this project';
  const canChangeMember = (rights) => canManage && (rights || []).every((key) => grantable.includes(key));

  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [addPersonUserId, setAddPersonUserId] = useState(null);
  const [addPersonRights, setAddPersonRights] = useState([]);

  const assignedUserIds = useMemo(
    () => (projectUsers || []).map((u) => Number(u?.id)).filter((id) => Number.isInteger(id)),
    [projectUsers]
  );

  const availableUserOptions = useMemo(() => {
    const assigned = new Set(assignedUserIds);
    return (users || [])
      .map((u) => ({ label: u?.name || '—', value: Number(u?.id) }))
      .filter((o) => Number.isInteger(o.value) && !assigned.has(o.value))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [users, assignedUserIds]);

  const closeAddPersonModal = () => {
    setAddPersonOpen(false);
  };

  // Runs once the dialog has faded out (onExited), so its fields don't empty while it closes.
  const resetAddPersonModal = () => {
    setAddPersonUserId(null);
    setAddPersonRights([]);
    setProjectMember.reset();
  };

  const submitAddPerson = async () => {
    if (!Number.isInteger(projectId)) return;
    const id = Number(addPersonUserId);
    if (!Number.isInteger(id) || assignedUserIds.includes(id)) return;

    await setProjectMember.mutateAsync({ projectId, userId: id, rights: addPersonRights });
    memberToast('Added to the project.');
    closeAddPersonModal();
  };

  // --- Change a member's rights ---
  const [rightsMember, setRightsMember] = useState(null);
  const [rightsDraft, setRightsDraft] = useState([]);
  // Open is separate from who it's for, so the dialog keeps its name and boxes while it fades out;
  // they're cleared once it has closed (onExited).
  const [rightsOpen, setRightsOpen] = useState(false);

  const openRightsModal = (member) => {
    setProjectMember.reset();
    setRightsMember(member);
    setRightsDraft(member.rights || []);
    setRightsOpen(true);
  };

  const closeRightsModal = () => setRightsOpen(false);

  const clearRightsModal = () => {
    setRightsMember(null);
    setRightsDraft([]);
    setProjectMember.reset();
  };

  const submitRights = async () => {
    if (!Number.isInteger(projectId) || !rightsMember) return;
    await setProjectMember.mutateAsync({ projectId, userId: rightsMember.id, rights: rightsDraft });
    memberToast(`${rightsMember.name}'s permissions updated.`);
    closeRightsModal();
  };

  // --- Remove person from the project ---
  // The confirmation pulls the person's footprint first so it can warn in proportion:
  // loudly when real forecast work would be orphaned, mildly when it is just a name.
  const [removePerson, setRemovePerson] = useState(null);
  const removePersonShown = useLastPresent(removePerson); // keeps the dialog steady while it fades out

  const {
    data: removeImpact,
    isLoading: removeImpactLoading,
    error: removeImpactError,
  } = useProjectUserAssignmentSummary(
    projectId,
    // Keyed on the shown person so the warning keeps its result while the dialog fades out.
    Number.isInteger(Number(removePersonShown?.id)) ? Number(removePersonShown.id) : null,
    { enabled: Boolean(removePerson) }
  );

  const removeIsDestructive = Boolean(removeImpact?.hasForecastHours || removeImpact?.hasAssignments);

  const submitRemovePerson = async () => {
    if (!Number.isInteger(projectId)) return;
    const id = Number(removePerson?.id);
    if (!Number.isInteger(id)) return;

    try {
      await removeProjectMember.mutateAsync({ projectId, userId: id });
    } finally {
      setRemovePerson(null);
    }
  };

  const projectPeopleDisplay = useMemo(() => {
    return (projectUsers || [])
      .map((u) => ({ id: Number(u?.id), name: u?.name || '—', rights: Array.isArray(u?.rights) ? u.rights : [] }))
      .filter((u) => Number.isInteger(u.id))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [projectUsers]);

  const initialsForName = (raw) => {
    const parts = String(raw || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '—';
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : '';
    const s = `${first}${last}`.toUpperCase();
    return s || '—';
  };

  const avatarStyleForId = (id) => {
    const n = Number(id);
    const safe = Number.isFinite(n) ? Math.abs(Math.trunc(n)) : 0;
    const hue = (safe * 137.508) % 360;
    const background = `hsl(${hue} 70% 90%)`;
    const borderColor = `hsl(${hue} 55% 78%)`;
    return { background, borderColor };
  };

  const {
    data: taskOrdersData,
    isLoading: taskOrdersLoading,
    error: taskOrdersError,
  } = useTaskOrders(projectId, { enabled: Number.isInteger(projectId) });

  const taskOrders = useMemo(() => (Array.isArray(taskOrdersData) ? taskOrdersData : []), [taskOrdersData]);

  const explicitTaskOrders = useMemo(() => {
    const rows = (taskOrders || []).filter((o) => !o?.isDefault);

    return rows.slice().sort((a, b) => {
      const aStart = parseDate10Parts(a?.startDate ?? a?.start_date);
      const bStart = parseDate10Parts(b?.startDate ?? b?.start_date);
      const aEnd = parseDate10Parts(a?.endDate ?? a?.end_date);
      const bEnd = parseDate10Parts(b?.endDate ?? b?.end_date);

      const aStartDay = aStart ? utcDayNumber(aStart) : null;
      const bStartDay = bStart ? utcDayNumber(bStart) : null;
      if (aStartDay !== null && bStartDay !== null && aStartDay !== bStartDay) return aStartDay - bStartDay;
      if (aStartDay !== null && bStartDay === null) return -1;
      if (aStartDay === null && bStartDay !== null) return 1;

      const aEndDay = aEnd ? utcDayNumber(aEnd) : null;
      const bEndDay = bEnd ? utcDayNumber(bEnd) : null;
      if (aEndDay !== null && bEndDay !== null && aEndDay !== bEndDay) return aEndDay - bEndDay;
      if (aEndDay !== null && bEndDay === null) return -1;
      if (aEndDay === null && bEndDay !== null) return 1;

      const aSort = Number.isFinite(Number(a?.sortOrder ?? a?.sort_order)) ? Number(a?.sortOrder ?? a?.sort_order) : null;
      const bSort = Number.isFinite(Number(b?.sortOrder ?? b?.sort_order)) ? Number(b?.sortOrder ?? b?.sort_order) : null;
      if (aSort !== null && bSort !== null && aSort !== bSort) return aSort - bSort;
      if (aSort !== null && bSort === null) return -1;
      if (aSort === null && bSort !== null) return 1;

      return String(a?.name || '').localeCompare(String(b?.name || ''));
    });
  }, [taskOrders]);
  const hasExplicitTaskOrders = explicitTaskOrders.length > 0;
  const rawProjectBudget = project?.budgetTarget ?? project?.budget_target;
  const projectBudgetValue =
    rawProjectBudget === null || rawProjectBudget === undefined ? null : Number(rawProjectBudget);
  const taskOrderBudgetTotal = useMemo(() => {
    return explicitTaskOrders.reduce((sum, order) => {
      const raw = order?.budgetTarget ?? order?.budget_target;
      const n = raw === null || raw === undefined ? 0 : Number(raw);
      return Number.isFinite(n) ? sum + n : sum;
    }, 0);
  }, [explicitTaskOrders]);
  const hasBudgetOverride = useMemo(() => {
    if (!hasExplicitTaskOrders) return false;
    if (!Number.isFinite(projectBudgetValue)) return false;
    return !amountsEqual(projectBudgetValue, taskOrderBudgetTotal);
  }, [hasExplicitTaskOrders, projectBudgetValue, taskOrderBudgetTotal]);
  const drivenBudgetValue = useMemo(() => {
    if (hasExplicitTaskOrders) return taskOrderBudgetTotal;
    return Number.isFinite(projectBudgetValue) ? projectBudgetValue : null;
  }, [hasExplicitTaskOrders, taskOrderBudgetTotal, projectBudgetValue]);
  const displayedProjectBudget = hasBudgetOverride
    ? (Number.isFinite(projectBudgetValue) ? projectBudgetValue : null)
    : drivenBudgetValue;

  const hasOnlyProjectTaskOrder = useMemo(
    () =>
      explicitTaskOrders.length === 1
      && String(explicitTaskOrders?.[0]?.name || '').trim().toLowerCase() === 'project',
    [explicitTaskOrders]
  );

  const canDeleteAnyTaskOrder = explicitTaskOrders.length > 1;

  const canConfirmTaskOrderDelete = useMemo(() => {
    const id = Number(pendingDeleteTaskOrder?.id);
    if (!Number.isInteger(id)) return false;
    if (!canDeleteAnyTaskOrder) return false;
    if (deleteTaskOrder.isPending) return false;
    const typed = String(taskOrderDeleteConfirmText || '').trim();
    return typed === String(pendingDeleteTaskOrder?.name || '').trim();
  }, [pendingDeleteTaskOrder, canDeleteAnyTaskOrder, deleteTaskOrder.isPending, taskOrderDeleteConfirmText]);

  const editTaskOrderSavedSnapshot = useMemo(() => {
    const row = pendingEditTaskOrder?.raw;
    if (!row) return null;
    const rawBudget = row?.budgetTarget ?? row?.budget_target;
    const budgetValue = rawBudget === null || rawBudget === undefined ? null : Number(rawBudget);
    return {
      name: String(row?.name || '').trim(),
      startDate: normalizeDate10(row?.startDate ?? row?.start_date),
      endDate: normalizeDate10(row?.endDate ?? row?.end_date),
      budgetTarget: Number.isFinite(budgetValue) ? budgetValue : null,
    };
  }, [pendingEditTaskOrder]);

  const editTaskOrderDraftSnapshot = useMemo(() => ({
    name: String(editTaskOrderName || '').trim(),
    startDate: normalizeDate10(editTaskOrderStartDate),
    endDate: normalizeDate10(editTaskOrderEndDate),
    budgetTarget: parseMoney(editTaskOrderBudgetTarget),
  }), [editTaskOrderName, editTaskOrderStartDate, editTaskOrderEndDate, editTaskOrderBudgetTarget]);

  const canSaveTaskOrderEdit = useMemo(() => {
    if (!Number.isInteger(projectId)) return false;
    if (!Number.isInteger(Number(pendingEditTaskOrder?.id))) return false;
    if (!String(editTaskOrderName || '').trim()) return false;
    const s = normalizeDate10(editTaskOrderStartDate);
    const e = normalizeDate10(editTaskOrderEndDate);
    if (!s || !e) return false;
    if (String(e).localeCompare(String(s)) < 0) return false;
    if (projectStartIso && String(s).localeCompare(String(projectStartIso)) < 0) return false;
    if (projectEndIso && String(e).localeCompare(String(projectEndIso)) > 0) return false;
    if (updateTaskOrder.isPending) return false;
    return JSON.stringify(editTaskOrderDraftSnapshot) !== JSON.stringify(editTaskOrderSavedSnapshot);
  }, [
    projectId,
    pendingEditTaskOrder,
    editTaskOrderName,
    editTaskOrderStartDate,
    editTaskOrderEndDate,
    projectStartIso,
    projectEndIso,
    updateTaskOrder.isPending,
    editTaskOrderDraftSnapshot,
    editTaskOrderSavedSnapshot,
  ]);

  const openTaskOrderEditModal = (taskOrder) => {
    const id = Number(taskOrder?.id);
    if (!Number.isInteger(id)) return;
    const rawBudget = taskOrder?.budgetTarget ?? taskOrder?.budget_target;
    setPendingEditTaskOrder({ id, raw: taskOrder });
    setEditTaskOrderName(String(taskOrder?.name || ''));
    setEditTaskOrderStartDate(normalizeDate10(taskOrder?.startDate ?? taskOrder?.start_date) ?? '');
    setEditTaskOrderEndDate(normalizeDate10(taskOrder?.endDate ?? taskOrder?.end_date) ?? '');
    setEditTaskOrderBudgetTarget(
      rawBudget === null || rawBudget === undefined ? '' : formatCurrency(rawBudget)
    );
    setEditTaskOrderOpen(true);
  };

  const closeTaskOrderEditModal = () => {
    if (updateTaskOrder.isPending) return;
    setEditTaskOrderOpen(false);
  };

  // Runs once the dialog has faded out (onExited), so its fields don't empty while it closes.
  const resetTaskOrderEditModal = () => {
    setPendingEditTaskOrder(null);
    setEditTaskOrderName('');
    setEditTaskOrderStartDate('');
    setEditTaskOrderEndDate('');
    setEditTaskOrderBudgetTarget('');
  };

  const goToActuals = () => {
    if (!Number.isInteger(projectId)) {
      navigate('/');
      return;
    }

    const list = Array.isArray(taskOrders) ? taskOrders : [];
    if (list.length === 0) {
      navigate(`/projects/${projectId}/actuals`);
      return;
    }

    const isNamed = (o) => {
      const name = typeof o?.name === 'string' ? o.name.trim() : '';
      return name.length > 0;
    };
    const explicitNamed = list.filter((o) => !o?.isDefault && isNamed(o));
    const candidates = explicitNamed.length > 0 ? explicitNamed : list;

    // If only one candidate remains, go straight there.
    if (candidates.length === 1) {
      const onlyId = Number(candidates[0]?.id);
      if (Number.isInteger(onlyId)) {
        navigate(`/projects/${projectId}/task-orders/${onlyId}/actuals`);
        return;
      }
    }

    const todayDay = utcDayNumber(todayParts());

    const normalized = candidates
      .map((o) => {
        const id = Number(o?.id);
        if (!Number.isInteger(id)) return null;
        const startIso = o?.startDate ?? o?.start_date;
        const endIso = o?.endDate ?? o?.end_date;
        const startParts = parseDate10Parts(startIso);
        const endParts = parseDate10Parts(endIso);
        const startDay = startParts ? utcDayNumber(startParts) : null;
        const endDay = endParts ? utcDayNumber(endParts) : null;
        return { id, startDay, endDay };
      })
      .filter(Boolean);

    const lowestId = normalized.reduce((min, o) => (min === null ? o.id : Math.min(min, o.id)), null);
    if (todayDay === null || normalized.length === 0) {
      if (Number.isInteger(lowestId)) {
        navigate(`/projects/${projectId}/task-orders/${lowestId}/actuals`);
        return;
      }
      navigate(`/projects/${projectId}/actuals`);
      return;
    }

    const active = normalized.filter(
      (o) => o.startDay !== null && o.endDay !== null && o.startDay <= todayDay && todayDay <= o.endDay
    );

    if (active.length > 0) {
      // If multiple are active (overlapping), pick the one ending soonest; tiebreaker: lowest id.
      const best = active
        .slice()
        .sort((a, b) => {
          const byEnd = Number(a.endDay) - Number(b.endDay);
          if (byEnd !== 0) return byEnd;
          return a.id - b.id;
        })[0];
      navigate(`/projects/${projectId}/task-orders/${best.id}/actuals`);
      return;
    }

    const scored = normalized
      .map((o) => {
        // Prefer full ranges; then partial; then fallback.
        const hasStart = o.startDay !== null;
        const hasEnd = o.endDay !== null;
        const kind = hasStart && hasEnd ? 0 : hasStart || hasEnd ? 1 : 2;

        let dist = Number.POSITIVE_INFINITY;
        if (hasStart && hasEnd) {
          dist = todayDay < o.startDay ? o.startDay - todayDay : todayDay > o.endDay ? todayDay - o.endDay : 0;
        } else if (hasStart) {
          dist = Math.abs(o.startDay - todayDay);
        } else if (hasEnd) {
          dist = Math.abs(o.endDay - todayDay);
        }

        return { ...o, kind, dist };
      })
      .filter((o) => Number.isFinite(o.dist));

    scored.sort((a, b) => {
      if (a.kind !== b.kind) return a.kind - b.kind;
      if (a.dist !== b.dist) return a.dist - b.dist;
      return a.id - b.id;
    });

    const best = scored[0];
    if (best && Number.isInteger(best.id)) {
      navigate(`/projects/${projectId}/task-orders/${best.id}/actuals`);
      return;
    }

    // Final fallback: lowest id.
    if (Number.isInteger(lowestId)) {
      navigate(`/projects/${projectId}/task-orders/${lowestId}/actuals`);
      return;
    }

    navigate(`/projects/${projectId}/actuals`);
  };

  // Both milestone columns render the same row; only the middle action differs
  // (auto milestones carry the Auto tag, custom ones an Edit button).
  const renderMilestoneRow = (m) => {
    const isAuto = Boolean(m?.is_auto);
    const milestoneId = m?.id;

    return (
      <div key={String(milestoneId)} className="ppc-pd-milestone">
        <div className="ppc-pd-milestone__lead">
          <span
            className={`ppc-pd-milestone__pip${isAuto ? ' ppc-pd-milestone__pip--auto' : ''}`}
            aria-hidden="true"
          />
          <div style={{ minWidth: 0 }}>
            <h4 className="ppc-pd-milestone__name">{String(m?.name || 'Milestone')}</h4>
            <p className="ppc-pd-milestone__date ppc-pd-num">{formatShortDate(m?.milestone_date)}</p>
          </div>
        </div>

        <div className="ppc-pd-milestone__actions">
          {isAuto ? (
            <Whisper
              placement="top"
              trigger="hover"
              speaker={<Tooltip>This milestone was automatically generated from task order dates</Tooltip>}
            >
              <span className="ppc-pd-tag">Auto</span>
            </Whisper>
          ) : (
            canManage ? (
              <button
                type="button"
                className="ppc-pd-linkbtn"
                onClick={() => openEditMilestoneModal(m)}
              >
                Edit
              </button>
            ) : null
          )}

          {canManage ? (
            <button
              type="button"
              className="ppc-pd-linkbtn ppc-pd-linkbtn--danger"
              disabled={deleteMilestone.isPending}
              onClick={() => {
                setPendingDeleteMilestone({
                  id: milestoneId,
                  name: String(m?.name || 'Milestone'),
                });
              }}
            >
              Delete
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  const goBack = () => navigate('/', { replace: true });

  if (!Number.isInteger(projectId)) {
    return (
      <div className="ppc-page">
        <Message type="error" showIcon>
          Invalid project.
        </Message>
      </div>
    );
  }

  return (
    <div className="ppc-page ppc-pd">
      <header className="ppc-pd__header">
        <div className="ppc-pd__title-wrap">
          <div className="ppc-pd__crumbs" aria-label="Breadcrumb">
            <button
              type="button"
              className="ppc-pd__crumb-link"
              onClick={goBack}
            >
              Projects
            </button>
            <i className="bi bi-chevron-right ppc-pd__crumb-sep" aria-hidden="true" />
            <span className="ppc-pd__crumb-current">{String(project?.name || 'Project')}</span>
          </div>

          <h1 className="ppc-pd__title">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              <i className="bi bi-collection" aria-hidden="true" />
              Project Details
            </span>
          </h1>
        </div>

        <div className="ppc-pd__actions">
          <button type="button" className="ppc-pd-btn" onClick={goBack}>
            <i className="bi bi-arrow-left" aria-hidden="true" />
            Back to projects
          </button>
          <div className="ppc-pd-segment" role="group" aria-label="Project views">
            <button
              type="button"
              className="ppc-pd-btn"
              onClick={() => navigate(`/projects/${projectId}/gantt`)}
            >
              Gantt
              <i className="bi bi-bar-chart-steps" aria-hidden="true" />
            </button>
            <button type="button" className="ppc-pd-btn" onClick={goToActuals}>
              Actuals
              <i className="bi bi-clipboard-data" aria-hidden="true" />
            </button>
            {/* Charts lives on its own route: the selection and the way back travel as query
                params, and taskOrderId=all keeps a stored task order from narrowing it. */}
            <button
              type="button"
              className="ppc-pd-btn"
              onClick={() => navigate(
                `/charts?projectId=${projectId}&taskOrderId=all`
                + `&from=project&fromProjectId=${projectId}`
              )}
            >
              Charts
              <i className="bi bi-bar-chart-line" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      {projectsError ? <Message type="error" showIcon>Failed to load projects.</Message> : null}
      {projectsLoading ? <div className="ppc-pd-section__note">Loading…</div> : null}
      {!project && !projectsLoading ? (
        <Message type="warning" showIcon>Project not found.</Message>
      ) : null}

      {project ? (
        <>
          <section className={`ppc-pd-hero${isHeroEditing ? ' is-editing' : ''}`}>
            {!isHeroEditing && canManage ? (
              <button
                type="button"
                className="ppc-pd-hero__edit-fab"
                onClick={startHeroEdit}
                title="Edit project details"
                aria-label="Edit project details"
              >
                <i className="bi bi-pencil" aria-hidden="true" />
                Edit
              </button>
            ) : null}

            <div className="ppc-pd-hero__identity">
              <div className="ppc-pd-hero__logo-field">
                <span className="ppc-pd-label">Logo</span>
                <div className="ppc-pd-hero__logo">
                  <div className="ppc-pd-hero__logo-media">
                    {project?.photoUrl ? (
                      <img src={project.photoUrl} alt="" />
                    ) : (
                      <span className="ppc-pd-hero__logo-fallback" aria-hidden="true">
                        {initialsForName(project?.name)}
                      </span>
                    )}
                  </div>
                  {canManage ? (
                    <button
                      type="button"
                      className={`ppc-pd-hero__photo-edit-fab${isHeroEditing ? ' is-always-visible' : ''}`}
                      onClick={() => setProjectPhotoModalOpen(true)}
                      title="Edit project photo"
                      aria-label="Edit project photo"
                    >
                      <i className="bi bi-pencil" aria-hidden="true" />
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="ppc-pd-hero__name-field">
                <span className="ppc-pd-label">Project name</span>
                {isHeroEditing ? (
                  <Input
                    value={editName}
                    onChange={setEditName}
                    placeholder="Project name"
                    disabled={updateProject.isPending}
                  />
                ) : (
                  <h2 className="ppc-pd-hero__name">{project?.name || '—'}</h2>
                )}
              </div>
            </div>

            <div className="ppc-pd-hero__stats">
              <div>
                <span className="ppc-pd-label">PIC</span>
                {isHeroEditing ? (
                  <SelectPicker
                    block
                    cleanable
                    searchable
                    className="ppc-pd-hero__pic-picker"
                    data={allUserOptions}
                    value={Number.isInteger(Number(editPicUserId)) ? Number(editPicUserId) : null}
                    onChange={(v) => setEditPicUserId(Number.isInteger(v) ? Number(v) : null)}
                    placeholder={usersLoading ? 'Loading users…' : 'Select PIC'}
                    disabled={usersLoading || updateProject.isPending}
                  />
                ) : (
                  <span className="ppc-pd-hero__stat">
                    {project?.picName ?? project?.pic_name ?? 'Unassigned'}
                  </span>
                )}
              </div>
              <div>
                <span className="ppc-pd-label">Contract number</span>
                {isHeroEditing ? (
                  <Input
                    value={editContractNumber}
                    onChange={setEditContractNumber}
                    placeholder="Contract number"
                    disabled={updateProject.isPending}
                  />
                ) : (
                  <span className="ppc-pd-hero__stat ppc-pd-num">
                    {project?.contractNumber ?? project?.contract_number ?? '—'}
                  </span>
                )}
              </div>
              <div>
                <span className="ppc-pd-label">Budget</span>
                {isHeroEditing ? (
                  <div className="ppc-pd-budget-edit-wrap">
                    <Whisper
                      ref={budgetDrivenWhisperRef}
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Driven by task order budgets</Tooltip>}
                      disabled={!(hasExplicitTaskOrders && !budgetOverrideEnabled)}
                    >
                      <div className="ppc-pd-budget-input-wrap">
                        <Input
                          value={editBudgetTarget}
                          onChange={(v) => {
                            const digits = digitsOnly(v);
                            if (!digits) {
                              setEditBudgetTarget('');
                              return;
                            }
                            const n = Number(digits);
                            setEditBudgetTarget(Number.isFinite(n) ? formatCurrency(n) : '');
                          }}
                          placeholder="$250,000"
                          className={hasExplicitTaskOrders ? 'ppc-pd-budget-input--with-icon' : ''}
                          disabled={!canFinance || updateProject.isPending || (hasExplicitTaskOrders && !budgetOverrideEnabled)}
                        />

                        {hasExplicitTaskOrders ? (
                          canFinance ? (
                            <button
                              type="button"
                              className={`ppc-pd-budget-toggle${budgetOverrideEnabled ? ' is-active' : ''}`}
                              onClick={() => {
                                budgetDrivenWhisperRef.current?.close?.();
                                const next = !budgetOverrideEnabled;
                                setBudgetOverrideEnabled(next);
                                if (!next) {
                                  setEditBudgetTarget(formatCurrency(taskOrderBudgetTotal));
                                }
                              }}
                              disabled={updateProject.isPending}
                              aria-label={budgetOverrideEnabled ? 'Disable budget override' : 'Enable budget override'}
                              title={budgetOverrideEnabled ? 'Disable override (use task order total)' : 'Enable manual budget override'}
                            >
                              <i className={`bi ${budgetOverrideEnabled ? 'bi-unlock-fill' : 'bi-lock-fill'}`} aria-hidden="true" />
                            </button>
                          ) : null
                        ) : null}
                      </div>
                    </Whisper>
                  </div>
                ) : (
                  <Whisper
                    placement="top"
                    trigger="hover"
                    speaker={<Tooltip>Driven by task order budgets</Tooltip>}
                    disabled={!(hasExplicitTaskOrders && !hasBudgetOverride)}
                  >
                    <span className="ppc-pd-hero__stat ppc-pd-num">
                      {displayedProjectBudget === null || displayedProjectBudget === undefined
                        ? '—'
                        : formatCurrency(displayedProjectBudget)}
                    </span>
                  </Whisper>
                )}
              </div>
              <div>
                <span className="ppc-pd-label">Location</span>
                {isHeroEditing ? (
                  <div className="ppc-pd-hero__city-edit">
                    <ProjectLocationPicker
                      city={editLocationCity}
                      latitude={editLocationLat}
                      longitude={editLocationLng}
                      onCityChange={setEditLocationCity}
                      onCoordinatesChange={(lat, lng) => {
                        setEditLocationLat(lat);
                        setEditLocationLng(lng);
                      }}
                      disabled={updateProject.isPending}
                    />
                  </div>
                ) : (
                  <span className="ppc-pd-hero__stat">
                    {project?.locationCity ?? project?.location_city ?? '—'}
                  </span>
                )}
              </div>

              {isHeroEditing ? (
                <>
                  <div>
                    <span className="ppc-pd-label">Start date</span>
                    <DatePicker
                      oneTap
                      cleanable
                      format="MM/dd/yyyy"
                      value={isoDateToDate(editStartDate)}
                      onChange={(d) => setEditStartDate(normalizeDate10(d) ?? '')}
                      style={{ width: '100%' }}
                      disabled={updateProject.isPending}
                    />
                  </div>
                  <div>
                    <span className="ppc-pd-label">End date</span>
                    <DatePicker
                      oneTap
                      cleanable
                      format="MM/dd/yyyy"
                      value={isoDateToDate(editEndDate)}
                      onChange={(d) => setEditEndDate(normalizeDate10(d) ?? '')}
                      style={{ width: '100%' }}
                      disabled={updateProject.isPending}
                    />
                  </div>
                </>
              ) : (
                <div>
                  <span className="ppc-pd-label">Duration</span>
                  <span className="ppc-pd-hero__stat">
                    {formatDuration(project?.startDate ?? project?.start_date, project?.endDate ?? project?.end_date)}
                  </span>
                </div>
              )}

              {!isHeroEditing ? <ProjectPursuitLinkField projectId={projectId} canEdit={canManage} /> : null}
            </div>

            {isHeroEditing ? (
              <div className="ppc-pd-hero__edit-actions">
                {/* Pushed to the far left, away from Save, so it is not the
                    button you reach for by reflex. */}
                {canManage ? (
                  <button
                    type="button"
                    className="ppc-pd-btn ppc-pd-btn--danger ppc-pd-hero__archive"
                    onClick={() => setArchiveOpen(true)}
                    disabled={updateProject.isPending || archiveProject.isPending}
                  >
                    <i className="bi bi-archive" aria-hidden="true" />
                    Archive project
                  </button>
                ) : null}

                <button
                  type="button"
                  className="ppc-pd-btn"
                  onClick={cancelHeroEdit}
                  disabled={updateProject.isPending}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="ppc-pd-btn ppc-pd-btn--primary"
                  onClick={() => void saveHeroEdit()}
                  disabled={!canSaveHero}
                >
                  Save changes
                </button>
              </div>
            ) : null}
          </section>

          <section className="ppc-pd-schedule">
            <div className="ppc-pd-schedule__end">
              <span className="ppc-pd-label">Start date</span>
              <span className="ppc-pd-schedule__date ppc-pd-num">
                {formatShortDate(project?.startDate ?? project?.start_date)}
              </span>
            </div>

            <div className="ppc-pd-schedule__rail">
              <div className="ppc-pd-schedule__track">
                <div
                  className="ppc-pd-schedule__fill"
                  style={{ width: `${scheduleProgress ? scheduleProgress.percent : 0}%` }}
                />
                <span className="ppc-pd-schedule__dot ppc-pd-schedule__dot--start" />
                <span
                  className="ppc-pd-schedule__dot ppc-pd-schedule__dot--end"
                  data-reached={scheduleProgress?.isComplete ? 'true' : 'false'}
                />
              </div>
              {scheduleProgress ? (
                <div className="ppc-pd-schedule__caption ppc-pd-num">
                  {scheduleProgress.isComplete
                    ? 'Schedule complete'
                    : `${scheduleProgress.percent.toFixed(0)}% elapsed · ${scheduleProgress.daysRemaining.toLocaleString()} day${scheduleProgress.daysRemaining === 1 ? '' : 's'} remaining`}
                </div>
              ) : null}
            </div>

            <div className="ppc-pd-schedule__end ppc-pd-schedule__end--right">
              <span className="ppc-pd-label">End date</span>
              <span className="ppc-pd-schedule__date ppc-pd-num">
                {formatShortDate(project?.endDate ?? project?.end_date)}
              </span>
            </div>
          </section>

          <ProjectTimesheetSection projectId={projectId} canEdit={canManage} />
        </>
      ) : null}

      <section className="ppc-pd-section">
        <div className="ppc-pd-section__head">
          <h3 className="ppc-pd-section__title">
            {hasOnlyProjectTaskOrder ? (
              'Project'
            ) : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <i className="bi bi-journal-text" aria-hidden="true" />
                Task orders
              </span>
            )}
          </h3>
          {canManage ? (
            <button
              type="button"
              className="ppc-pd-iconbtn"
              onClick={() => {
                setNewTaskOrderName('');
                setNewTaskOrderStartDate(projectStartIso ?? '');
                setNewTaskOrderEndDate(projectEndIso ?? '');
                setNewTaskOrderBudgetTarget('');
                setAddTaskOrderOpen(true);
              }}
              title="Add task order"
              aria-label="Add task order"
            >
              <i className="bi bi-plus-lg" aria-hidden="true" />
            </button>
          ) : null}
        </div>

        {taskOrdersLoading ? <div className="ppc-pd-section__note">Loading task orders…</div> : null}
        {taskOrdersError ? (
          <Message type="error" showIcon>Failed to load task orders.</Message>
        ) : null}
        {!hasExplicitTaskOrders && !taskOrdersLoading ? (
          <Message type="info" showIcon>No explicit task orders for this project.</Message>
        ) : null}

        {createTaskOrder.error ? (
          <Message type="error" showIcon>
            {String(createTaskOrder.error?.message || 'Failed to create task order.')}
          </Message>
        ) : null}

        {hasExplicitTaskOrders ? (
          <div className={`ppc-pd-to-grid${explicitTaskOrders.length === 1 ? ' ppc-pd-to-grid--single' : ''}`}>
            {explicitTaskOrders.map((o) => {
              const id = Number(o?.id);
              const name = o?.name || '—';
              const dates = o?.startDate && o?.endDate ? `${o.startDate} → ${o.endDate}` : null;
              const budget = o?.budgetTarget === null || o?.budgetTarget === undefined
                ? null
                : formatCurrency(o.budgetTarget);
              const remaining = formatTimeRemaining(o?.startDate ?? o?.start_date, o?.endDate ?? o?.end_date);

              const goTo = () => {
                if (!Number.isInteger(id)) return;
                navigate(`/projects/${projectId}/task-orders/${id}`);
              };

              return (
                <div
                  key={String(id)}
                  className="ppc-pd-to-card"
                  role="button"
                  tabIndex={0}
                  onClick={goTo}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      goTo();
                    }
                  }}
                >
                  {remaining ? (
                    <span
                      className={`ppc-pd-to-card__badge${remaining === 'Ended' ? ' ppc-pd-to-card__badge--ended' : ''}`}
                    >
                      {remaining}
                    </span>
                  ) : null}

                  <div className="ppc-pd-to-card__body">
                    <h4 className="ppc-pd-to-card__name">{name}</h4>
                    <p className="ppc-pd-to-card__dates ppc-pd-num">{dates ?? 'Dates not set'}</p>
                    <p className="ppc-pd-to-card__budget ppc-pd-num">
                      {budget ? `Budget ${budget}` : 'Budget —'}
                    </p>
                  </div>


                  <button
                    type="button"
                    className="ppc-pd-btn ppc-pd-btn--sm ppc-pd-btn--primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      goTo();
                    }}
                    disabled={!Number.isInteger(id)}
                  >
                    View Task Order
                    <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
                  </button>

                  {!o?.isDefault && canManage ? (
                    <button
                      type="button"
                      className="ppc-pd-to-card__edit"
                      title={`Edit ${name}`}
                      aria-label={`Edit ${name}`}
                      disabled={updateTaskOrder.isPending}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openTaskOrderEditModal(o);
                      }}
                    >
                      <i className="bi bi-pencil" aria-hidden="true" />
                    </button>
                  ) : null}

                  {!o?.isDefault && canDeleteAnyTaskOrder && canManage ? (
                    <button
                      type="button"
                      className="ppc-pd-to-card__delete"
                      title={`Delete ${name}`}
                      aria-label={`Delete ${name}`}
                      disabled={deleteTaskOrder.isPending}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setPendingDeleteTaskOrder({ id, name });
                        setTaskOrderDeleteConfirmText('');
                      }}
                    >
                      <i className="bi bi-trash" aria-hidden="true" />
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </section>

      <Modal open={addTaskOrderOpen} onClose={() => setAddTaskOrderOpen(false)} size="sm">
        <Modal.Header>
          <Modal.Title>Add Task Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: '#666' }}>Name</div>
              <Input
                value={newTaskOrderName}
                onChange={setNewTaskOrderName}
                placeholder="e.g. Task Order 001"
                disabled={createTaskOrder.isPending}
              />
            </div>

            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <div>
                <div style={{ fontSize: 12, color: '#666' }}>Start date</div>
                <DatePicker
                  oneTap
                  cleanable
                  format="MM/dd/yyyy"
                  value={isoDateToDate(newTaskOrderStartDate)}
                  onChange={(d) => setNewTaskOrderStartDate(normalizeDate10(d) ?? '')}
                  style={{ width: '100%' }}
                  disabled={createTaskOrder.isPending}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#666' }}>End date</div>
                <DatePicker
                  oneTap
                  cleanable
                  format="MM/dd/yyyy"
                  value={isoDateToDate(newTaskOrderEndDate)}
                  onChange={(d) => setNewTaskOrderEndDate(normalizeDate10(d) ?? '')}
                  style={{ width: '100%' }}
                  disabled={createTaskOrder.isPending}
                />
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666' }}>Budget</div>
              <Input
                value={newTaskOrderBudgetTarget}
                onChange={(v) => {
                  const digits = digitsOnly(v);
                  if (!digits) {
                    setNewTaskOrderBudgetTarget('');
                    return;
                  }
                  const n = Number(digits);
                  setNewTaskOrderBudgetTarget(Number.isFinite(n) ? formatCurrency(n) : '');
                }}
                placeholder="$50,000"
                disabled={!canFinance || createTaskOrder.isPending}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setAddTaskOrderOpen(false)} disabled={createTaskOrder.isPending}>
            Cancel
          </NsiButton>
          <NsiButton
            variant="primary"
            loading={createTaskOrder.isPending}
            disabled={
              !String(newTaskOrderName || '').trim()
              || !normalizeDate10(newTaskOrderStartDate)
              || !normalizeDate10(newTaskOrderEndDate)
              || (String(normalizeDate10(newTaskOrderEndDate)).localeCompare(String(normalizeDate10(newTaskOrderStartDate))) < 0)
              || (projectStartIso && String(normalizeDate10(newTaskOrderStartDate)).localeCompare(String(projectStartIso)) < 0)
              || (projectEndIso && String(normalizeDate10(newTaskOrderEndDate)).localeCompare(String(projectEndIso)) > 0)
            }
            onClick={async () => {
              if (!Number.isInteger(projectId)) return;
              const payload = {
                name: String(newTaskOrderName || '').trim(),
                startDate: normalizeDate10(newTaskOrderStartDate),
                endDate: normalizeDate10(newTaskOrderEndDate),
                ...(canFinance ? { budgetTarget: parseMoney(newTaskOrderBudgetTarget) } : {}),
              };
              await createTaskOrder.mutateAsync({ projectId, payload });
              setAddTaskOrderOpen(false);
            }}
          >
            Create
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal open={editTaskOrderOpen} onClose={closeTaskOrderEditModal} onExited={resetTaskOrderEditModal} size="sm">
        <Modal.Header>
          <Modal.Title>Edit Task Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: '#666' }}>Name</div>
              <Input
                value={editTaskOrderName}
                onChange={setEditTaskOrderName}
                placeholder="Task order name"
                disabled={updateTaskOrder.isPending}
              />
            </div>

            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <div>
                <div style={{ fontSize: 12, color: '#666' }}>Start date</div>
                <DatePicker
                  oneTap
                  cleanable
                  format="MM/dd/yyyy"
                  value={isoDateToDate(editTaskOrderStartDate)}
                  onChange={(d) => setEditTaskOrderStartDate(normalizeDate10(d) ?? '')}
                  style={{ width: '100%' }}
                  disabled={updateTaskOrder.isPending}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#666' }}>End date</div>
                <DatePicker
                  oneTap
                  cleanable
                  format="MM/dd/yyyy"
                  value={isoDateToDate(editTaskOrderEndDate)}
                  onChange={(d) => setEditTaskOrderEndDate(normalizeDate10(d) ?? '')}
                  style={{ width: '100%' }}
                  disabled={updateTaskOrder.isPending}
                />
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666' }}>Budget</div>
              <Input
                value={editTaskOrderBudgetTarget}
                onChange={(v) => {
                  const digits = digitsOnly(v);
                  if (!digits) {
                    setEditTaskOrderBudgetTarget('');
                    return;
                  }
                  const n = Number(digits);
                  setEditTaskOrderBudgetTarget(Number.isFinite(n) ? formatCurrency(n) : '');
                }}
                placeholder="$50,000"
                disabled={!canFinance || updateTaskOrder.isPending}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={closeTaskOrderEditModal} disabled={updateTaskOrder.isPending}>
            Cancel
          </NsiButton>
          <NsiButton
            variant="primary"
            loading={updateTaskOrder.isPending}
            disabled={!canSaveTaskOrderEdit}
            onClick={async () => {
              const taskOrderId = Number(pendingEditTaskOrder?.id);
              if (!Number.isInteger(taskOrderId) || !Number.isInteger(projectId)) return;
              const payload = {
                name: String(editTaskOrderName || '').trim(),
                startDate: normalizeDate10(editTaskOrderStartDate),
                endDate: normalizeDate10(editTaskOrderEndDate),
                ...(canFinance ? { budgetTarget: parseMoney(editTaskOrderBudgetTarget) } : {}),
              };
              await updateTaskOrder.mutateAsync({ projectId, taskOrderId, payload });
              closeTaskOrderEditModal();
            }}
          >
            Save changes
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal open={projectPhotoModalOpen} onClose={() => setProjectPhotoModalOpen(false)} size="sm">
        <Modal.Header>
          <Modal.Title>Edit project photo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <input
            ref={projectPhotoInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => {
              void onSelectProjectPhoto(e);
            }}
            style={{ display: 'none' }}
            disabled={uploadProjectPhoto.isPending || deleteProjectPhoto.isPending}
          />

          <div className="ppc-pd-photo-modal__preview-wrap">
            <div
              className={`ppc-pd-photo-modal__preview${project?.photoUrl ? '' : ' ppc-pd-photo-modal__preview--empty'}`}
              role={!project?.photoUrl ? 'button' : undefined}
              tabIndex={!project?.photoUrl ? 0 : undefined}
              onClick={() => {
                if (!project?.photoUrl) projectPhotoInputRef.current?.click();
              }}
              onKeyDown={(e) => {
                if (!project?.photoUrl && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  projectPhotoInputRef.current?.click();
                }
              }}
              onDragOver={(e) => {
                if (!project?.photoUrl) e.preventDefault();
              }}
              onDrop={(e) => {
                if (project?.photoUrl) return;
                e.preventDefault();
                const file = e.dataTransfer?.files?.[0];
                void uploadProjectPhotoFile(file);
              }}
              aria-label={!project?.photoUrl ? 'Upload project photo by clicking or dropping an image' : undefined}
            >
              {project?.photoUrl ? (
                <img src={project.photoUrl} alt="Project" />
              ) : (
                <div className="ppc-pd-photo-modal__empty">
                  <i className="bi bi-cloud-arrow-up" aria-hidden="true" />
                  <span>Drag and drop an image here</span>
                  <span className="ppc-pd-photo-modal__empty-sub">or click to browse</span>
                </div>
              )}
            </div>
          </div>

          <div className="ppc-pd-photo-modal__help">PNG/JPG/WebP up to 5MB.</div>

          {project?.photoUrl ? (
            <div className="ppc-pd-photo-modal__actions">
              <Button
                appearance="ghost"
                onClick={() => projectPhotoInputRef.current?.click()}
                loading={uploadProjectPhoto.isPending}
                disabled={uploadProjectPhoto.isPending || deleteProjectPhoto.isPending}
              >
                Replace
              </Button>
              <Button
                appearance="ghost"
                color="red"
                onClick={() => setConfirmPhotoDeleteOpen(true)}
                loading={deleteProjectPhoto.isPending}
                disabled={uploadProjectPhoto.isPending || deleteProjectPhoto.isPending}
              >
                Delete
              </Button>
            </div>
          ) : null}
        </Modal.Body>
      </Modal>

      <Modal
        open={confirmPhotoDeleteOpen}
        onClose={() => {
          if (deleteProjectPhoto.isPending) return;
          setConfirmPhotoDeleteOpen(false);
        }}
        size="xs"
      >
        <Modal.Header>
          <Modal.Title>Delete project photo?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="error" showIcon>
            This will permanently remove the project image.
          </Message>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton
            disabled={deleteProjectPhoto.isPending}
            onClick={() => setConfirmPhotoDeleteOpen(false)}
          >
            Cancel
          </NsiButton>
          <NsiButton
            variant="danger-solid"
            loading={deleteProjectPhoto.isPending}
            onClick={async () => {
              if (!Number.isInteger(projectId)) return;
              await deleteProjectPhoto.mutateAsync({ projectId });
            }}
          >
            Delete photo
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal
        open={Boolean(pendingDeleteTaskOrder)}
        onClose={() => {
          if (deleteTaskOrder.isPending) return;
          setPendingDeleteTaskOrder(null);
          setTaskOrderDeleteConfirmText('');
        }}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Delete task order?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="error" showIcon>
            You are deleting <strong>{pendingDeleteTaskOrderShown?.name || 'this task order'}</strong>. This action is
            permanent and will delete all related tasks, activity forecasts, actuals, budgets, and invoices.
          </Message>
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
              Type the task order name to confirm deletion
            </div>
            <Input
              value={taskOrderDeleteConfirmText}
              onChange={setTaskOrderDeleteConfirmText}
              placeholder={String(pendingDeleteTaskOrderShown?.name || '')}
              disabled={deleteTaskOrder.isPending}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton
            disabled={deleteTaskOrder.isPending}
            onClick={() => {
              setPendingDeleteTaskOrder(null);
              setTaskOrderDeleteConfirmText('');
            }}
          >
            Cancel
          </NsiButton>
          <NsiButton
            variant="danger-solid"
            loading={deleteTaskOrder.isPending}
            disabled={!canConfirmTaskOrderDelete}
            onClick={async () => {
              const id = Number(pendingDeleteTaskOrderShown?.id);
              if (!Number.isInteger(id) || !Number.isInteger(projectId)) return;
              await deleteTaskOrder.mutateAsync({
                projectId,
                taskOrderId: id,
                taskOrderName: String(pendingDeleteTaskOrderShown?.name || 'Task order'),
              });
              setPendingDeleteTaskOrder(null);
              setTaskOrderDeleteConfirmText('');
            }}
          >
            Delete task order
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <section className="ppc-pd-section">
        <div className="ppc-pd-section__head">
          <div className="ppc-pd-section__heading">
            <h3 className="ppc-pd-section__title">People</h3>
            <span className="ppc-pd-count ppc-pd-num">{projectPeopleDisplay.length}</span>
          </div>
          {canManage ? (
            <button
              type="button"
              className="ppc-pd-iconbtn"
              onClick={() => setAddPersonOpen(true)}
              disabled={membersBusy}
              title="Add person"
              aria-label="Add person"
            >
              <i className="bi bi-plus-lg" aria-hidden="true" />
            </button>
          ) : null}
        </div>

        {projectUsersError ? (
          <Message type="error" showIcon>Failed to load project people.</Message>
        ) : null}
        {projectUsersLoading ? <div className="ppc-pd-section__note">Loading people…</div> : null}
        {projectPeopleDisplay.length === 0 && !projectUsersLoading ? (
          <Message type="info" showIcon>No one is assigned to this project yet.</Message>
        ) : null}

        {projectPeopleDisplay.length > 0 ? (
          <div className="ppc-pd-people-grid">
            {projectPeopleDisplay.map((p) => {
              const s = avatarStyleForId(p.id);
              return (
                <div key={p.id} className="ppc-pd-person">
                  <div className="ppc-pd-person__head">
                    <span
                      className="ppc-pd-person__avatar"
                      style={{ background: s.background, borderColor: s.borderColor }}
                      aria-hidden="true"
                    >
                      {initialsForName(p.name)}
                    </span>
                    <span className="ppc-pd-person__name" title={p.name}>{p.name}</span>
                    {/* The PIC is always on the project, so they cannot be removed. Nor can someone
                        holding a permission the signed-in user doesn't. */}
                    {p.id !== Number(project?.picUserId) && canChangeMember(p.rights) ? (
                      <button
                        type="button"
                        className="ppc-pd-person__remove"
                        onClick={() => setRemovePerson({ id: p.id, name: p.name })}
                        disabled={membersBusy}
                        title={`Remove ${p.name} from this project`}
                        aria-label={`Remove ${p.name} from this project`}
                      >
                        <i className="bi bi-x-lg" aria-hidden="true" />
                      </button>
                    ) : null}
                  </div>
                  {canManage || permissions.can('rates.manage') ? (
                    <div className="ppc-pd-person__actions">
                      {canManage ? (
                        <button
                          type="button"
                          className="ppc-pd-person__action"
                          onClick={() => openRightsModal(p)}
                          disabled={membersBusy}
                          title={`Permissions on this project: ${rightsTitle(p.rights)}`}
                        >
                          <i className="bi bi-shield-lock" aria-hidden="true" />
                          Manage permissions
                        </button>
                      ) : null}
                      {permissions.can('rates.manage') ? (
                        <button
                          type="button"
                          className="ppc-pd-person__action"
                          onClick={() => navigate(`/users/${p.id}?from=project&fromProjectId=${projectId}#rates`)}
                          title={`Open ${p.name}'s billing rates`}
                        >
                          <i className="bi bi-cash-coin" aria-hidden="true" />
                          Manage rates
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}

        {usersError ? <Message type="error" showIcon>Failed to load users.</Message> : null}
        {usersLoading && allUserOptions.length === 0 ? (
          <div className="ppc-pd-section__note">User directory is still loading.</div>
        ) : null}
      </section>

      <section className="ppc-pd-section">
        <div className="ppc-pd-section__head">
          <h3 className="ppc-pd-section__title">Milestones</h3>
          {canManage ? (
            <button
              type="button"
              className="ppc-pd-iconbtn"
              onClick={openCreateMilestoneModal}
              title="Add custom milestone"
              aria-label="Add custom milestone"
            >
              <i className="bi bi-plus-lg" aria-hidden="true" />
            </button>
          ) : null}
        </div>

        {milestonesError ? (
          <Message type="error" showIcon>Failed to load milestones.</Message>
        ) : null}
        {milestonesLoading ? <div className="ppc-pd-section__note">Loading milestones…</div> : null}

        {!milestonesLoading ? (
          <div className="ppc-pd-milestone-cols">
            <div className="ppc-pd-milestone-col">
              <div className="ppc-pd-milestone-col__head">
                <span className="ppc-pd-milestone-col__title">Auto generated</span>
                <span className="ppc-pd-count ppc-pd-num">{autoMilestones.length}</span>
              </div>

              {autoMilestones.length > 0 ? (
                <div className="ppc-pd-milestones">
                  {autoMilestones.map((m) => renderMilestoneRow(m))}
                </div>
              ) : (
                <p className="ppc-pd-empty">
                  No auto milestones. These are generated from task order start and end dates.
                </p>
              )}
            </div>

            <div className="ppc-pd-milestone-col">
              <div className="ppc-pd-milestone-col__head">
                <span className="ppc-pd-milestone-col__title">Custom</span>
                <span className="ppc-pd-count ppc-pd-num">{customMilestones.length}</span>
              </div>

              {customMilestones.length > 0 ? (
                <div className="ppc-pd-milestones">
                  {customMilestones.map((m) => renderMilestoneRow(m))}
                </div>
              ) : (
                <p className="ppc-pd-empty">
                  You haven&apos;t set any custom milestones yet. Use the + above to add one.
                </p>
              )}
            </div>
          </div>
        ) : null}

        {createMilestone.error ? (
          <Message type="error" showIcon>
            {String(createMilestone.error?.message || 'Failed to add milestone.')}
          </Message>
        ) : null}

        {updateMilestone.error ? (
          <Message type="error" showIcon>
            {String(updateMilestone.error?.message || 'Failed to update milestone.')}
          </Message>
        ) : null}
      </section>

      <Modal
        open={archiveOpen}
        onClose={() => {
          if (archiveProject.isPending) return;
          setArchiveOpen(false);
        }}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Archive this project?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="warning" showIcon>
            <strong>{project?.name || 'This project'}</strong> will be removed from the
            projects list, along with its task orders and forecasts.
          </Message>
          <div className="ppc-pd-section__note" style={{ marginTop: 10 }}>
            Nothing is deleted — you can restore it from Configuration → Project Management.
          </div>
          {archiveProject.isError ? (
            <Message type="error" showIcon style={{ marginTop: 10 }}>
              {String(archiveProject.error?.message || 'Failed to archive project.')}
            </Message>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <NsiButton
            onClick={() => setArchiveOpen(false)}
            disabled={archiveProject.isPending}
          >
            Cancel
          </NsiButton>
          <NsiButton
            variant="danger-solid"
            loading={archiveProject.isPending}
            disabled={archiveProject.isPending}
            onClick={() => void submitArchiveProject()}
          >
            Archive
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal open={addPersonOpen} onClose={closeAddPersonModal} onExited={resetAddPersonModal} size="sm">
        <Modal.Header>
          <Modal.Title>Add person</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Person</div>
              <SelectPicker
                data={availableUserOptions}
                value={Number.isInteger(addPersonUserId) ? addPersonUserId : null}
                onChange={(v) => setAddPersonUserId(Number.isInteger(v) ? v : null)}
                searchable
                cleanable
                style={{ width: '100%' }}
                placeholder={usersLoading ? 'Loading people…' : 'Select a person'}
                disabled={usersLoading || membersBusy}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Permissions on this project</div>
              <ProjectRightsChecklist
                rights={rightsCatalog}
                value={addPersonRights}
                onChange={setAddPersonRights}
                grantable={grantable}
                disabled={membersBusy}
              />
            </div>

            {!usersLoading && availableUserOptions.length === 0 ? (
              <Message type="info" showIcon>
                Everyone in the directory is already on this project.
              </Message>
            ) : null}

            {setProjectMember.error ? (
              <Message type="error" showIcon>
                {String(setProjectMember.error?.message || 'Failed to add person.')}
              </Message>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={closeAddPersonModal} disabled={membersBusy}>
            Cancel
          </NsiButton>
          <NsiButton
            variant="primary"
            onClick={() => void submitAddPerson().catch(() => {})}
            loading={setProjectMember.isPending}
            disabled={!Number.isInteger(addPersonUserId) || membersBusy}
          >
            Add
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal open={rightsOpen} onClose={() => !membersBusy && closeRightsModal()} onExited={clearRightsModal} size="sm">
        <Modal.Header>
          <Modal.Title>{rightsMember?.name || 'Member'}'s permissions on this project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'grid', gap: 12 }}>
            <ProjectRightsChecklist
              rights={rightsCatalog}
              value={rightsDraft}
              onChange={setRightsDraft}
              grantable={grantable}
              disabled={membersBusy}
            />
            {setProjectMember.error ? (
              <Message type="error" showIcon>
                {String(setProjectMember.error?.message || 'Failed to update permissions.')}
              </Message>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={closeRightsModal} disabled={membersBusy}>
            Cancel
          </NsiButton>
          <NsiButton
            variant="primary"
            onClick={() => void submitRights().catch(() => {})}
            loading={setProjectMember.isPending}
            disabled={membersBusy || [...rightsDraft].sort().join() === [...(rightsMember?.rights || [])].sort().join()}
          >
            Save
          </NsiButton>
        </Modal.Footer>
      </Modal>

<Modal
        open={Boolean(removePerson)}
        onClose={() => {
          if (membersBusy) return;
          setRemovePerson(null);
        }}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>
            {removeIsDestructive ? 'Remove person with assigned work?' : 'Remove person?'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="ppc-pd-removebody">
            {removeImpactLoading ? (
              <div className="ppc-pd-section__note">Checking what {removePersonShown?.name || 'this person'} is assigned to…</div>
            ) : null}

            {removeImpactError ? (
              <Message type="error" showIcon>
                Could not check this person's assignments. Remove with caution — they may
                still have forecast hours or disciplines on this project.
              </Message>
            ) : null}

            {!removeImpactLoading && !removeImpactError && removeIsDestructive ? (
              <div className="ppc-pd-impact">
                <div className="ppc-pd-impact__head">
                  <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
                  This person has assigned work on this project
                </div>

                {removeImpact?.hasForecastHours ? (
                  <>
                    <div className="ppc-pd-impact__hours ppc-pd-num">
                      {formatHours(removeImpact.forecastHours)}
                    </div>
                    <div className="ppc-pd-impact__hours-caption">
                      of forecasted hours will be removed along with their project rates.
                    </div>
                  </>
                ) : null}

                {removeImpact?.forecastTasks?.length > 0 ? (
                  <div className="ppc-pd-impact__group">
                    <div className="ppc-pd-impact__group-label">Forecast hours in</div>
                    <ul className="ppc-pd-impact__list">
                      {removeImpact.forecastTasks.map((t) => (
                        <li key={`f-${t.taskId}`}>
                          {t.taskName || `Discipline ${t.taskId}`}
                          <span className="ppc-pd-num">{` — ${formatHours(t.hours)}`}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {removeImpact?.ledTasks?.length > 0 ? (
                  <div className="ppc-pd-impact__group">
                    <div className="ppc-pd-impact__group-label">
                      Lead of {removeImpact.ledTasks.length === 1 ? 'discipline' : 'disciplines'}
                    </div>
                    <ul className="ppc-pd-impact__list">
                      {removeImpact.ledTasks.map((t) => (
                        <li key={`lt-${t.id}`}>{t.name || `Discipline ${t.id}`}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {removeImpact?.ledActivities?.length > 0 ? (
                  <div className="ppc-pd-impact__group">
                    <div className="ppc-pd-impact__group-label">
                      Lead of {removeImpact.ledActivities.length === 1 ? 'activity' : 'activities'}
                    </div>
                    <ul className="ppc-pd-impact__list">
                      {removeImpact.ledActivities.map((a) => (
                        <li key={`la-${a.id}`}>
                          {a.name || `Activity ${a.id}`}
                          {a.taskName ? <span className="ppc-pd-section__note">{` · ${a.taskName}`}</span> : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {removeImpact?.supportedActivities?.length > 0 ? (
                  <div className="ppc-pd-impact__group">
                    <div className="ppc-pd-impact__group-label">
                      Supporting {removeImpact.supportedActivities.length === 1 ? 'activity' : 'activities'}
                    </div>
                    <ul className="ppc-pd-impact__list">
                      {removeImpact.supportedActivities.map((a) => (
                        <li key={`sa-${a.id}`}>
                          {a.name || `Activity ${a.id}`}
                          {a.taskName ? <span className="ppc-pd-section__note">{` · ${a.taskName}`}</span> : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : null}

            {!removeImpactLoading && !removeImpactError && !removeIsDestructive ? (
              <Message type="warning" showIcon>
                <strong>{removePersonShown?.name || 'This person'}</strong> has no forecast hours and
                is not assigned to any disciplines on this project. Removing them only takes
                their name off the project.
              </Message>
            ) : null}

            {removeIsDestructive ? (
              <div className="ppc-pd-section__note">
                Removing <strong>{removePersonShown?.name || 'this person'}</strong> deletes their
                projected hours and project rates for this project. This cannot be undone.
              </div>
            ) : null}

            {removeProjectMember.error ? (
              <Message type="error" showIcon>
                {String(removeProjectMember.error?.message || 'Failed to remove person.')}
              </Message>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton
            onClick={() => setRemovePerson(null)}
            disabled={membersBusy}
          >
            Cancel
          </NsiButton>
          <NsiButton
            variant="danger-solid"
            loading={removeProjectMember.isPending}
            disabled={removeImpactLoading || membersBusy}
            onClick={() => void submitRemovePerson().catch(() => {})}
          >
            {removeIsDestructive ? 'Remove anyway' : 'Remove'}
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal open={milestoneModalOpen} onClose={closeMilestoneModal} onExited={resetMilestoneModal} size="xs">
        <Modal.Header>
          <Modal.Title>{milestoneModalMode === 'edit' ? 'Edit milestone' : 'Add milestone'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: '#666' }}>Name</div>
              <Input value={milestoneNameInput} onChange={setMilestoneNameInput} placeholder="Milestone name" />
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666' }}>Date</div>
              <DatePicker
                oneTap
                cleanable={false}
                format="MM/dd/yyyy"
                value={isoDateToDate(normalizeDate10(milestoneDateInput))}
                onChange={(d) => setMilestoneDateInput(normalizeDate10(d) ?? '')}
                style={{ width: '100%' }}
                shouldDisableDate={(date) => {
                  const iso = normalizeDate10(date);
                  if (!iso) return false;
                  if (projectStartIso && String(iso).localeCompare(String(projectStartIso)) < 0) return true;
                  if (projectEndIso && String(iso).localeCompare(String(projectEndIso)) > 0) return true;
                  return false;
                }}
              />
            </div>

            {!isMilestoneInProjectRange && milestoneDateIso ? (
              <Message type="warning" showIcon>
                Milestone date must be within the project range.
              </Message>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={closeMilestoneModal}>
            Cancel
          </NsiButton>
          <NsiButton
            variant="primary"
            onClick={() => void saveMilestone()}
            loading={createMilestone.isPending || updateMilestone.isPending}
            disabled={!milestoneFormValid}
          >
            Save
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal
        open={Boolean(pendingDeleteMilestone)}
        onClose={() => {
          if (deleteMilestone.isPending) return;
          setPendingDeleteMilestone(null);
        }}
        size="xs"
      >
        <Modal.Header>
          <Modal.Title>Delete milestone?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="warning" showIcon>
            This will permanently delete <strong>{pendingDeleteMilestoneShown?.name || 'this milestone'}</strong>.
          </Message>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton
            disabled={deleteMilestone.isPending}
            onClick={() => setPendingDeleteMilestone(null)}
          >
            Cancel
          </NsiButton>
          <NsiButton
            variant="danger-solid"
            loading={deleteMilestone.isPending}
            disabled={deleteMilestone.isPending}
            onClick={() => void confirmDeleteMilestone()}
          >
            Delete
          </NsiButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
